import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import Status from '../status/status';
import { PrescriptionStatus, Prescription, ActionStatus } from '../../types/data';
import Input from '../../ui/input';
import InputDate from '../../ui/input-date';
import Select from 'src/pages/DashboardPage/vpo/ui/select';
import { Directions, Users, Locations, AnswerOptions } from '../../mocks/data';
import { useForm, Controller, useFieldArray, FormProvider, FieldValues } from 'react-hook-form';
import TGButton from 'src/components/ui/TGButton';
import { DateValueType } from 'react-tailwindcss-datepicker';
import { PlusCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import Violation from '../violation/violation';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { UserSelect } from '../UserSelect/UserSelect';
import { selectItems } from 'src/modules/spItems/selectors';
import { SingleUserSelect } from '../SingleUserSelect/SingleUserSelect';
import { selectCurrentUser } from 'src/modules/users/selector';
import TextAreaVPO from 'src/pages/DashboardPage/vpo/ui/textArea';
import AlertInformation from '../alert-information/alert-information';
import FileUploader from '../upLoadFiles/FileUploader';
import { emailAPI } from 'src/utils/api.utils';
import moment from 'moment';

import { TooltipItem } from 'src/utils/Tooltip';
import { determinePrescriptionStatus, formatEmptyPrescriptionBody, getPrescriptionStatusId } from '../../utils/prescriptionStatus';
import { paramsListName, siteUrl } from 'src/api/config';
import { getItemsRequest } from 'src/modules/spItems/api';
import { emailQueueListName } from 'src/api/config/email.config';
import { mode } from 'src/constants';
import { addDataGOS, setLastDocId, updateDataGOS, deleteDataGOS } from '../../store/addForms/actions';
import { selectLastDocId, selectLastAddedId } from '../../store/addForms/selector';
import { selectModelData } from '../../store/filterData/selector';
import { fetchData } from '../../store/filterData/actions';
import { transformDataForFilters } from '../../utils/utilsGOS';
import { calculateActionStatus } from '../../utils/utils';
import { myTaskActionsListName } from 'src/api/config/myTasksActions.config';
import React from 'react';

const { sendEmailStrapi, deleteEmail } = emailAPI();

interface ViolationType {
  violations: string;
}

type FormValues = Omit<Prescription, 'id' | 'status' | 'actualClosingDatePrescription' | 'identifiedViolations'> & {
  violations: ViolationType[];
};

type PrescriptionFormProps = {
  initialValues?: Omit<Prescription, 'status' | 'identifiedViolations'>;
  isEdit?: boolean;
  isNewForm?: boolean;
  toggleDoing?: (value: boolean) => void;
};

function PrescriptionForm({ initialValues, isEdit, toggleDoing, isNewForm = false }: PrescriptionFormProps) {
  const dispatch = useAppDispatch();
  const [prescriptionStatus, setPrescriptionStatus] = useState<PrescriptionStatus | string>(initialValues?.status?.name || 'В процессе');
  const [prescriptionStatusId, setPrescriptionStatusId] = useState(initialValues?.status?.id || null);
  const [actionStatus, setActionStatus] = useState<ActionStatus>(initialValues?.actionStatus || 'Нет действий');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isModalOpenAlert, setModalOpenAlert] = useState<boolean>(false);
  const [fileCount, setFileCount] = useState(0);
  const [isFormDisabled, setIsFormDisabled] = useState(true);
  // Исправляем логику: поля должны быть заблокированы если форма заблокирована ИЛИ запись в архиве
  const isFieldDisabled = isFormDisabled || initialValues?.IsArchived === true;

  const locationFilterData = useAppSelector((state: any) => selectModelData(state, 'gos-locations'));
  const directionFilterData = useAppSelector((state: any) => selectModelData(state, 'gos-directions'));

const directionFilter = React.useMemo(() => transformDataForFilters(directionFilterData), [directionFilterData]);
const locationFilter = React.useMemo(() => transformDataForFilters(locationFilterData), [locationFilterData]);

  const prescriptionStatusFilter = useAppSelector((state: any) => selectModelData(state, 'gos-prescription-statuses'));
  const actionStatusFilter = useAppSelector((state: any) => selectModelData(state, 'gos-action-statuses'));
  const lastIDForm = useAppSelector((state) => selectLastAddedId(state, 'gos-prescriptions'));
  const currentUser = useAppSelector((state) => selectCurrentUser(state));

  const params: any = useAppSelector((state) => selectItems(state, paramsListName));

const prescriptionsDocumentId = useAppSelector((state) => selectLastDocId(state, 'gos-prescriptions'));

  const stateGlobal = useAppSelector((state) => state);

  const allMyTaskActions = useAppSelector((state) => selectItems(state, myTaskActionsListName));

  //! Получаю локации, направлений, статусы предписаний и действий
    useEffect(() => {
      dispatch(
        fetchData(
          'gos-locations',
          {},
          {
            page: 1,
            pageSize: 100,
          }
        )
      );
      dispatch(
        fetchData(
          'gos-directions',
          {},
          {
            page: 1,
            pageSize: 100,
          }
        )
      );
      dispatch(
        fetchData(
          'gos-prescription-statuses',
          {},
          {
            page: 1,
            pageSize: 100,
          }
        )
      );
      dispatch(
        fetchData(
          'gos-action-statuses',
          {},
          {
            page: 1,
            pageSize: 100,
          }
        )
      );
    }, [dispatch]);

  const isSavingRef = useRef(false);
  const lastIDFormRef = useRef<number | null>(null);


  const { infoGOS } = useAppSelector(selectCurrentUser);

  const defaultResponsible: any = {
    id: currentUser.id,
    email: currentUser.email,
    title: currentUser.displayName,
    avatar: currentUser.avatar,
    shortName: currentUser.shortName,
  };

  const defaultValues: FormValues = {
    prescriptionNumber: null,
    direction: null,
    responsible: defaultResponsible,
    location: null,
    dateVerification: null,
    deadlineExecutionOrder: null,
    agencyInspection: '',
    scopeVerification: '',
    typeVerification: '',
    fieldActivity: '',
    departmentsAudit: '',
    nameRegulatoryDocument: '',
    interestedParties: [],
    amountFine: '',
    fundsEliminationComments: '',
    comment: '',
    dateUpdate: null,
    violations: [],
    periodicNotificationUpdateDate: AnswerOptions[0],
    frequencyRepeatedNotifications: 7,
  };

  const methods = useForm<FormValues>({
    defaultValues: initialValues || defaultValues,
    mode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors, isDirty },
  } = methods;

  useEffect(() => {
    let state = true; // по умолчанию форма заблокирована

    // Если пользователь является редактором
    if (infoGOS.editor) {
      // Если у записи есть локация
      if (initialValues?.location) {
        // Проверяем, входит ли локация в список фабрик пользователя
        if (infoGOS.factoryIds && infoGOS.factoryIds.indexOf(initialValues.location.id) >= 0) {
          state = false; // разблокируем форму
        }
      } else {
        // Если локации нет, то форма должна быть разблокирована для создания новой записи
        state = false;
      }
    }
    if (infoGOS.admin) {
      state = false;
    }

    setIsFormDisabled(state);
  }, [infoGOS, initialValues?.location]);

  const watchDeadline = watch('deadlineExecutionOrder');

  useEffect(() => {
    if (watchDeadline?.startDate) {
      const deadlineDate = new Date(watchDeadline.startDate);
      const newStatus = determinePrescriptionStatus(deadlineDate);
      setPrescriptionStatus(newStatus);
    }
  }, [watchDeadline]);

  useEffect(() => {
    lastIDFormRef.current = lastIDForm;
  }, [lastIDForm]);

  //! обновление записи
  const onSubmit = (data: FieldValues) => {
    isSavingRef.current = true;

    // Определяем статус предписания на основе срока и статуса действий
    let deadlineDate: Date | null = null;
    if (data.deadlineExecutionOrder?.startDate) {
      deadlineDate = new Date(data.deadlineExecutionOrder.startDate);
    }

    const statusName = determinePrescriptionStatus(deadlineDate, actionStatus);
    const statusId = getPrescriptionStatusId(statusName as PrescriptionStatus, prescriptionStatusFilter);

    const statusIdCurrent = data.status?.id;

    let actionStatusId = 1;

    if (actionStatusFilter && actionStatusFilter.length > 0) {
      actionStatusFilter.map((item: any) => {
        if (item.Title === actionStatus) {
          actionStatusId = item.id;
        }
      });
    }

    const requestBody: any = {
      ID: isEdit ? initialValues?.id : (lastIDForm),
      Title: `prescriptionGos`,
      PrescriptionNumber: data.prescriptionNumber,
      Direction: data.direction.id,
      ResponsibleEmail: data.responsible?.email || data.Responsible?.Email,
      InterestedPartiesEmails: data.interestedParties.map((u: any) => u.email),
      GOSLocation: data.location.id,
      DateVerification: new Date(data.dateVerification.startDate),
      DeadlineExecutionOrder: new Date(data.deadlineExecutionOrder.startDate),
      AgencyInspection: data.agencyInspection,
      ScopeVerification: data.scopeVerification,
      TypeVerification: data.typeVerification,
      FieldActivity: data.fieldActivity,
      DepartmentsAudit: data.departmentsAudit,
      NameRegulatoryDocument: data.nameRegulatoryDocument,
      AmountFine: data.amountFine,
      FundsEliminationComments: data.fundsEliminationComments,
      Comments: data.comment || '',
      DateUpdate: data.dateUpdate?.startDate ? new Date(data.dateUpdate.startDate) : null,
      FrequencyRepeatedNotifications: data.periodicNotificationUpdateDate?.name === 'Да' ? data.frequencyRepeatedNotifications : 0,
      PeriodicNotificationUpdateDate: !data.dateUpdate ? '-' : data.periodicNotificationUpdateDate.name === 'Да' ? 'да' : 'нет',
      PrescriptionStatus: statusId,
      // ActionStatus: actionStatusId, //! удалить и с БД тоже
    };

  

    let emails = [data.responsible?.email];

    if (data.interestedParties.length > 0) {
      emails = [...emails, ...data.interestedParties.map((item: any) => item.email)];
    }
    let emailIDs: any = [];

    const timestamp = moment().unix();

    getItemsRequest({
      listName: emailQueueListName,
      filters: {
        GosId: '' + String(requestBody.ID),
        Subject: ['Гос Требования | Предписание просрочено', 'Гос Требования | Уведомление о предписании'],
      },
    })
      .then((results) => {
        const { items, nextPage: newNextPage } = results;

        if (items.length > 0) {
          emailIDs = items.map((item) => ({
            ID: Number(item.ID),
          }));
        }

        if (statusId === 3 && prescriptionStatusId !== 2) {  // 3 = Просрочено, 2 = Выполнено
          let emailBody = {
            Body: `Здравствуйте!<br/><br/>
              Примите к сведению, что предписание № <a href="${siteUrl}/dashboard/4/?prescriptionCard=${requestBody.ID}">${data.prescriptionNumber}</a> просрочено: <br/><br/>
              <ul>
                <li>Завод: ${data.location.name}</li>
                <li>Направление: ${data.direction.name}</li>
                <li>Дата проведения проверки: ${moment(data.dateVerification.startDate).format('DD.MM.YYYY')}</li>
                <li>Гос. учреждение, проводившее проверку: ${data.agencyInspection}</li>
                <li>Срок исполнения по предписанию: <strong style="color:red;">${moment(data.deadlineExecutionOrder.startDate).format('DD.MM.YYYY')}</span></li>
              </ul>`,
            DateToBeSentAt: moment().subtract(25, 'hours').toDate(),
            Title: 'Гос Требования | Предписание просрочено',
            Subject: 'Гос Требования | Предписание просрочено',
            Recepients: emails.join(', '),
            RepeatAt: (params && Number(params.find((param: any) => param.Key === 'PrescriptionExpired')['Value'].trim())) || 1440 * 7,
            RepeatTimes: 100000,
            RepeatTimesValue: 0,
            GosId: String(requestBody.ID),
            CheckDoc: String(timestamp),
          };
          Promise.all([sendEmailStrapi(emailBody)]);
        }

        if (statusId === 1 && prescriptionStatusId !== 2) {  // 1 = В процессе, 2 = Выполнено
          let emailBody2 = {
            Body: `Здравствуйте!<br/><br/>
              Примите к сведению, что у предписания № <a href="${siteUrl}/dashboard/4/?prescriptionCard=${requestBody.ID}">${
              data.prescriptionNumber
            }</a> близится срок исполнения по предписанию: <br/><br/>
              <ul>
                <li>Завод: ${data.location.name}</li>
                <li>Направление: ${data.direction.name}</li>
                <li>Дата проведения проверки: ${moment(data.dateVerification.startDate).format('DD.MM.YYYY')}</li>
                <li>Гос. учреждение, проводившее проверку: ${data.agencyInspection}</li>
                <li>Срок исполнения по предписанию: <strong style="color:red;">${moment(data.deadlineExecutionOrder.startDate).format('DD.MM.YYYY')}</span></li>
              </ul>`,
            DateToBeSentAt: moment(requestBody.DeadlineExecutionOrder).subtract(2, 'months').toDate(),
            Title: 'Гос Требования | Уведомление о предписании',
            Subject: 'Гос Требования | Уведомление о предписании',
            Recepients: emails.join(', '),
            RepeatAt: 0,
            RepeatTimes: 1,
            RepeatTimesValue: 0,
            GosId: String(requestBody.ID),
            CheckDoc: String(timestamp),
          };

          if (requestBody.DateUpdate) {
            emailBody2.DateToBeSentAt = moment(requestBody.DateUpdate).toDate();
            emailBody2.RepeatAt = 1440 * requestBody.FrequencyRepeatedNotifications;
            emailBody2.RepeatTimes = requestBody.FrequencyRepeatedNotifications > 0 ? 100000 : 1;
          }

          Promise.all([sendEmailStrapi(emailBody2)]);
        }

        const { ID, Title, ...strapiData } = requestBody;

        if (isEdit) {
          //! редактирование заполненной записи
          dispatch(updateDataGOS('gos-prescriptions', initialValues?.docId, strapiData));
        } else {
          //! редактирование пустой записи, которая автосохранилась при входе
             dispatch(updateDataGOS('gos-prescriptions', prescriptionsDocumentId, strapiData));
          let emailBody3 = {
            Body: `Здравствуйте!<br/><br/>
              Примите к сведению, что предписание № <a href="${siteUrl}/dashboard/4/?prescriptionCard=${requestBody.ID}">${data.prescriptionNumber}</a> зарегистрировано: <br/><br/>
              <ul>
                <li>Завод: ${data.location.name}</li>
                <li>Направление: ${data.direction.name}</li>
                <li>Дата проведения проверки: ${moment(data.dateVerification.startDate).format('DD.MM.YYYY')}</li>
                <li>Гос. учреждение, проводившее проверку: ${data.agencyInspection}</li>
                <li>Срок исполнения по предписанию: ${moment(data.deadlineExecutionOrder.startDate).format('DD.MM.YYYY')}</li>
              </ul>`,
            DateToBeSentAt: moment().subtract(12, 'hours').toDate(),
            Title: 'Гос Требования | Предписание зарегистрировано',
            Subject: 'Гос Требования | Предписание зарегистрировано',
            Recepients: emails.join(', '),
            RepeatAt: 0,
            RepeatTimes: 1,
            RepeatTimesValue: 0,
            GosId: String(requestBody.ID),
            CheckDoc: String(timestamp),
          };
          Promise.all([sendEmailStrapi(emailBody3)]);
        }

        // console.log('deleteEmail', data, initialValues, statusIdCurrent);

        deleteEmail(emailIDs);
      })
      .catch((error) => {});
    toggleDoing(false);
  };

  useEffect(() => {
    return () => {
      if (!isSavingRef.current && isNewForm && prescriptionsDocumentId) {
        dispatch(deleteDataGOS('gos-prescriptions', prescriptionsDocumentId));
      }
    };
  }, [prescriptionsDocumentId]);

  //! Дата обновления предписания
  const dateUpdate = watch('dateUpdate');
  const isAddDateUpdate = dateUpdate?.startDate;

  const isAddNotification = watch('periodicNotificationUpdateDate');

  const watchedValues = watch();

  const requiredFields: (keyof FormValues)[] = [
    'prescriptionNumber',
    'direction',
    'responsible',
    'location',
    'dateVerification',
    'deadlineExecutionOrder',
    'agencyInspection',
    'scopeVerification',
    'typeVerification',
    'fieldActivity',
    'departmentsAudit',
    'nameRegulatoryDocument',
    'interestedParties',
  ];

  const isSubmitDisabled = requiredFields.some((field) => {
    const value = watchedValues[field];

    if (Array.isArray(value)) {
      return value.length === 0 || errors[field];
    }
    return !value || errors[field];
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'violations',
  });

  //! при нажатии на отмену, автосохраненная запись удаляется
  const cancel = useCallback(() => {
    isSavingRef.current = false;
    if (isNewForm && prescriptionsDocumentId) {
      dispatch(deleteDataGOS('gos-prescriptions', prescriptionsDocumentId));
    }

    toggleDoing(false);
  }, [isNewForm, prescriptionsDocumentId, dispatch, toggleDoing]);

  const onCancelEditClick = () => {
    if (isEdit && isDirty) {
      setModalOpenAlert(true);
    } else {
      toggleDoing(false);
    }
  };

  useEffect(() => {
    if (lastIDForm && lastIDForm > 0) {
      setIsDialogOpen(true);
    }
  }, [lastIDForm]);

  //! автосохранение пустой записи при открытии модалки
  useEffect(() => {
    if (isNewForm) {
      const requestBody = formatEmptyPrescriptionBody(prescriptionStatus, actionStatus, defaultResponsible, prescriptionStatusFilter, actionStatusFilter);
      dispatch(addDataGOS('gos-prescriptions', requestBody));
    }
  }, [isNewForm]);

  const handleRemoveViolation = (index: number, violationId?: number, violationDocumentId?: string) => {
    if (isEdit && violationDocumentId) {
      dispatch(deleteDataGOS('gos-violations', violationDocumentId));
    }

    remove(index);
    if (initialValues?.violations) {
      initialValues.violations = initialValues.violations.filter((_, i) => i !== index);
    }
  };

  useEffect(() => {
    if (isEdit && initialValues?.violations && initialValues?.violations?.length > 0) {
      remove();
    }
  }, []);

  useEffect(() => {
    if (stateGlobal?.spitems?.updateStatus?.MyTaskActions == 'SUCCESS') {
      let statusID: any = null;

      if (initialValues?.violations && initialValues?.violations.length > 0 && initialValues?.violations[0].actions && initialValues?.violations[0].actions.length > 0) {
        statusID = 3;
        initialValues?.violations[0].actions
          .filter((action: any) => action.ImportantHashTag && action.ImportantHashTag.trim() === `#Предписание_${initialValues?.violations[0].id}`.trim())
          .map((action: any) => {
            if (action && action.Status && (action.Status.ID == 1 || action.Status.ID == 2)) {
              statusID = 1;
            }
            // setActionStatus(action.Status.Title);
          });
      }

      if (watchDeadline?.startDate) {
        const deadlineDate = new Date(watchDeadline.startDate);
        const newStatus = determinePrescriptionStatus(deadlineDate);
        if (newStatus == 'Просрочено' && (statusID == 1 || statusID == 3)) {
          statusID = 2;
        }
      }

      if (stateGlobal?.spitems?.elements?.item?.MyTaskActions?.Status?.get_lookupValue && stateGlobal?.spitems?.elements?.item?.MyTaskActions?.Status?.get_lookupValue()) {
        setActionStatus(stateGlobal?.spitems?.elements?.item?.MyTaskActions?.Status?.get_lookupValue());
      }

      setPrescriptionStatusId(statusID);

      if (statusID && (isEdit ? initialValues?.docId : prescriptionsDocumentId)) {
        dispatch(
          updateDataGOS(
            'gos-prescriptions',
            isEdit ? initialValues?.docId : prescriptionsDocumentId,
            {
              PrescriptionStatus: statusID,
            }
          )
        );
      }
    }
  }, [stateGlobal?.spitems?.elements?.item?.MyTaskActions?.Status?.get_lookupId()]);

  //! Обновление статуса действий на основе всех действий во всех нарушениях
  useEffect(() => {
    if (initialValues?.violations && initialValues?.violations.length > 0 && allMyTaskActions) {

      const violationIds = initialValues.violations.map((v: any) => v.id);

      const getViolationId = (hashTag: string): number | undefined => {
        const regex = /#Предписание_(\d+)/;
        const match = hashTag?.match(regex);
        return match ? Number(match[1]) : undefined;
      };

      const relatedActions = allMyTaskActions.filter((action: any) => {
        const violationId = getViolationId(action.ImportantHashTag);
        return violationId && violationIds.includes(violationId);
      });

      const newActionStatus = calculateActionStatus(relatedActions);

      if (newActionStatus !== actionStatus) {
        setActionStatus(newActionStatus as ActionStatus);
      }
    }
  }, [initialValues?.violations, allMyTaskActions]);

  return (
    <>
      <div className='p-10'>
        <div className='flex justify-between items-center mb-8'>
          <h2 className='text-3xl'>Предписание</h2>
          <div className='flex items-center gap-4'>
            {/* <Status type={prescriptionStatus} /> */}
            {initialValues && initialValues.status && <Status type={initialValues.status.name} />}
            {initialValues?.actualClosingDatePrescription?.startDate && (
              <TooltipItem position='top-left' tooltipsText={'Фактическая дата закрытия предписания'}>
                <span className='text-base text-primaryGray'>{new Date(initialValues.actualClosingDatePrescription.startDate).toLocaleDateString()}</span>
              </TooltipItem>
            )}
          </div>
        </div>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <section>
              <div className='grid grid-cols-2 gap-5'>
                <Controller
                  control={control}
                  name='prescriptionNumber'
                  rules={{
                    required: 'Обязательное поле',
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextAreaVPO
                      dataField={{
                        type: 'textArea',
                        require: true,
                        width: 'w-full',
                        title: '№ предписания',
                        key: 'prescriptionNumber',
                        placeholder: 'Введите № предписания',
                      }}
                      value={field.value}
                      onChange={field.onChange}
                      errors={{ prescriptionNumber: error }}
                      minHeight={48}
                      disabled={isFieldDisabled}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name='direction'
                  render={({ field: { onChange, value } }) => (
                    <Select
                      data={
                        directionFilter?.map((item) => ({
                          id: item.ID,
                          name: item.Title || '',
                        })) || []
                      }
                      width='w-full'
                      selected={value}
                      label='Направление'
                      placeholder='Выберите направление'
                      required
                      onChange={onChange}
                      disabled={isFieldDisabled}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name='responsible'
                  defaultValue={defaultResponsible}
                  render={({ field: { onChange, value } }) => <SingleUserSelect selectedUser={value} onUserSelect={onChange} label='Ответственный' required disabled={isFieldDisabled} />}
                />

                <Controller
                  control={control}
                  name='location'
                  render={({ field: { onChange, value } }) => (
                    <Select
                      width='w-full'
                      data={
                        (!infoGOS.admin && infoGOS.editor && infoGOS.factory) ||
                        locationFilter?.map((item) => ({
                          id: item.ID,
                          name: item.Title || '',
                        })) ||
                        []
                      }
                      selected={value}
                      label='Локация'
                      placeholder='Выберите локацию'
                      required
                      onChange={onChange}
                      disabled={isFieldDisabled}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name='dateVerification'
                  render={({ field: { onChange, value } }) => <InputDate label='Дата проведения проверки' value={value as DateValueType} onChange={onChange} required disabled={isFieldDisabled} />}
                />

                <Controller
                  control={control}
                  name='deadlineExecutionOrder'
                  render={({ field: { onChange, value } }) => (
                    <InputDate label='Срок исполнения по предписанию' value={value as DateValueType} onChange={onChange} required disabled={isFieldDisabled} />
                  )}
                />
              </div>

              <div className='mt-5 flex flex-col gap-5'>
                <Controller
                  control={control}
                  name='agencyInspection'
                  render={({ field, fieldState: { error } }) => (
                    <TextAreaVPO
                      dataField={{
                        type: 'textArea',
                        require: true,
                        width: 'w-full',
                        title: 'Госучреждение, проводившее проверку',
                        key: 'agencyInspection',
                        placeholder: 'Введите госучреждение',
                      }}
                      value={field.value}
                      onChange={field.onChange}
                      errors={{ agencyInspection: error }}
                      minHeight={48}
                      disabled={isFieldDisabled}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name='scopeVerification'
                  render={({ field, fieldState: { error } }) => (
                    <TextAreaVPO
                      dataField={{
                        type: 'textArea',
                        require: true,
                        width: 'w-full',
                        title: 'Область проверки, инспектор',
                        key: 'scopeVerification',
                        placeholder: 'Введите область проверки, инспектор',
                      }}
                      value={field.value}
                      onChange={field.onChange}
                      errors={{ scopeVerification: error }}
                      minHeight={48}
                      disabled={isFieldDisabled}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name='typeVerification'
                  render={({ field, fieldState: { error } }) => (
                    <TextAreaVPO
                      dataField={{
                        type: 'textArea',
                        require: true,
                        width: 'w-full',
                        title: 'Цель, вид проверки',
                        key: 'typeVerification',
                        placeholder: 'Введите цель, вид проверки',
                      }}
                      value={field.value}
                      onChange={field.onChange}
                      errors={{ typeVerification: error }}
                      minHeight={48}
                      disabled={isFieldDisabled}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name='fieldActivity'
                  render={({ field, fieldState: { error } }) => (
                    <TextAreaVPO
                      dataField={{
                        type: 'textArea',
                        require: true,
                        width: 'w-full',
                        title: 'Сфера деятельности',
                        key: 'fieldActivity',
                        placeholder: 'Введите сферу деятельности',
                      }}
                      value={field.value}
                      onChange={field.onChange}
                      errors={{ fieldActivity: error }}
                      minHeight={48}
                      disabled={isFieldDisabled}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name='departmentsAudit'
                  render={({ field, fieldState: { error } }) => (
                    <TextAreaVPO
                      dataField={{
                        type: 'textArea',
                        require: true,
                        width: 'w-full',
                        title: 'Подразделения, в которых проводилась проверка',
                        key: 'departmentsAudit',
                        placeholder: 'Введите подразделение',
                      }}
                      value={field.value}
                      onChange={field.onChange}
                      errors={{ departmentsAudit: error }}
                      minHeight={48}
                      disabled={isFieldDisabled}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name='nameRegulatoryDocument'
                  render={({ field, fieldState: { error } }) => (
                    <TextAreaVPO
                      dataField={{
                        type: 'textArea',
                        require: true,
                        width: 'w-full',
                        title: 'Наименование нормативного документа и номер пункта, требования которого нарушены',
                        key: 'nameRegulatoryDocument',
                        placeholder: 'Введите наименование нормативного документа и номер пункта',
                      }}
                      value={field.value}
                      onChange={field.onChange}
                      errors={{ nameRegulatoryDocument: error }}
                      minHeight={48}
                      disabled={isFieldDisabled}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name='interestedParties'
                  render={({ field: { onChange, value } }) => (
                    <div className='w-full'>
                      <div className='mb-2 flex justify-between'></div>
                      <UserSelect label='Заинтересованные лица' required={true} selectedUsers={value} onUserFilterChange={onChange} disabled={isFieldDisabled} />
                    </div>
                  )}
                />
              </div>
            </section>
          </form>
          <section className='mt-8'>
            <div className='flex items-center'>
              <h4 className='text-base text-primaryGray'>Выявленные нарушения · {(initialValues?.violations?.length || 0) + fields.length}</h4>
              <span className='ml-auto mr-5'>Статус действий</span>
              <Status type={actionStatus} />
            </div>
            {isEdit &&
              initialValues?.violations?.map((field: any, index) => (
                <div key={field.id} className='flex items-start'>
                  <div className='w-full mb-3'>
                    <Violation isEdit index={index} value={field.text} formId={initialValues.id} violationId={field.id} violationDocumentId={field.documentId} disabled={isFieldDisabled} />
                  </div>
                  <TGButton
                    type='button'
                    icon={<TrashIcon width={20} height={20} />}
                    onClick={() => handleRemoveViolation(index, field.id, field.documentId)}
                    className={`translate-y-[6px] ml-2 ${initialValues.violations.length > 1 ? '' : 'hidden'}`}
                    disabled={isFieldDisabled}
                  />
                </div>
              ))}
            {fields.map((field: any, index) => (
              <div key={field.id} className='flex items-start'>
                <div className='w-full mb-3'>
                  <Violation index={index + (initialValues?.violations?.length || 0)} value={field.value} formId={initialValues?.id || lastIDForm} disabled={isFieldDisabled} />
                </div>
                <TGButton
                  type='button'
                  icon={<TrashIcon width={20} height={20} />}
                  onClick={() => handleRemoveViolation(index)}
                  className={`translate-y-[6px] ml-2 ${fields.length > 1 ? '' : 'hidden'}`}
                  disabled={isFieldDisabled}
                />
              </div>
            ))}

            <TGButton
              className='w-full text-primaryGray mt-4'
              type='button'
              icon={<PlusCircleIcon color='#637381' width={20} height={20} />}
              onClick={() => append({ violations: '' })}
              disabled={isFieldDisabled}
            >
              Добавить нарушение
            </TGButton>
          </section>
          <form onSubmit={handleSubmit(onSubmit)}>
            <section className='mt-8'>
              <div className='flex items-center mb-4'>
                <h4 className='text-base text-primaryGray'>Обновление</h4>
              </div>

              <div className='flex flex-col gap-5'>
                <Controller
                  control={control}
                  name='dateUpdate'
                  render={({ field: { onChange, value } }) => (
                    <InputDate
                      label='Дата обновления'
                      value={value as DateValueType}
                      onChange={onChange}
                      description='В указанную дату придет уведомление со ссылкой на предписание на почту ответственного и заинтересованных лиц'
                      disabled={isFieldDisabled}
                    />
                  )}
                />

                {isAddDateUpdate && (
                  <Controller
                    control={control}
                    name='periodicNotificationUpdateDate'
                    render={({ field: { onChange, value } }) => (
                      <Select data={AnswerOptions} selected={value} label='Необходимо периодическое уведомление после даты обновления?' required onChange={onChange} disabled={isFieldDisabled} />
                    )}
                  />
                )}

                {isAddNotification?.name === 'Да' && isAddDateUpdate && (
                  <Controller
                    control={control}
                    name='frequencyRepeatedNotifications'
                    render={({ field: { onChange, value } }) => <Input label='Периодичность повторных уведомлений в днях' value={value} onChange={onChange} disabled={isFieldDisabled} />}
                  />
                )}
              </div>
            </section>

            <section className='mt-8'>
              <div className='flex items-center mb-4'>
                <h4 className='text-base text-primaryGray'>Дополнительная информация</h4>
              </div>

              <div className='flex flex-col gap-5'>
                <Controller
                  control={control}
                  name='amountFine'
                  render={({ field: { onChange, value } }) => <Input label='Размер штрафа' value={value} placeholder='Введите размер штрафа' onChange={onChange} disabled={isFieldDisabled} />}
                />

                <Controller
                  control={control}
                  name='fundsEliminationComments'
                  render={({ field: { onChange, value } }) => (
                    <Input label='Необходимые средства на устранение замечаний' value={value} placeholder='Введите количество средств' onChange={onChange} disabled={isFieldDisabled} />
                  )}
                />

                <Controller
                  control={control}
                  name='comment'
                  render={({ field, fieldState: { error } }) => {
                    const textValue = field.value?.replace(/<[^>]*>/g, '') || '';
                    return (
                      <TextAreaVPO
                        dataField={{
                          type: 'textArea',
                          require: false,
                          width: 'w-full',
                          title: 'Комментарий',
                          key: 'comment',
                          placeholder: 'Введите комментарий',
                        }}
                        value={textValue}
                        onChange={(newValue) => field.onChange(newValue)}
                        errors={{}}
                        minHeight={48}
                        disabled={isFieldDisabled}
                      />
                    );
                  }}
                />
              </div>
            </section>
          </form>
          <section className='mt-8'>
            <div className='flex items-center mb-4'>
              <h4 className='text-base text-[#637381]'>Вложения · {fileCount}</h4>
            </div>
            <FileUploader prescriptionId={initialValues?.id ?? lastIDForm} onFileCountChange={setFileCount} />
          </section>

          <section className='mt-8 flex justify-between'>
            <TGButton type='button' onClick={isEdit ? onCancelEditClick : cancel}>
              Отмена
            </TGButton>

            {!isFormDisabled && (
              <>
                {isEdit ? (
                  <TGButton type='submit' color='bluedark' disabled={isSubmitDisabled} onClick={handleSubmit(onSubmit)}>
                    Сохранить
                  </TGButton>
                ) : (
                  <TGButton type='submit' color='bluedark' disabled={isSubmitDisabled} onClick={handleSubmit(onSubmit)}>
                    Сохранить
                  </TGButton>
                )}
              </>
            )}
          </section>
        </FormProvider>

        {isEdit && (
          <AlertInformation
            isOpen={isModalOpenAlert}
            title='Вы действительно хотите отменить изменения?'
            text="При нажатии 'Отмена' возврат действий будет невозможен"
            onClose={() => setModalOpenAlert(false)}
            onCloseForm={() => toggleDoing(false)}
          />
        )}
      </div>
    </>
  );
}

export default PrescriptionForm;
